/*
  The design tokens a theme may override, and the CSS custom property each one maps to.

  THIS FILE MIRRORS TWO OTHERS

  The names here are the ones in `src/styles/brand.css` with their prefixes removed, and
  they are the same names `Tracer.Core.Branding.ThemeTokens` will accept. Three copies of
  one list is two too many in principle; in practice the API has to validate a name it
  can refuse before a write, the stylesheet has to declare a default the browser can fall
  back to, and this file has to know which prefix to put back on. There is a unit test on
  the C# side that asserts the two token sets match, and a token added anywhere has to be
  added in all three.

  The semantic colours — error, warn, ok and their washes — are deliberately absent, and
  the API refuses them by name. Semantic colour outranks brand colour everywhere in this
  product: a blocking QC finding has to read as blocking whoever the product is sold as.
*/

export const COLOR_TOKENS = [
  'primary',
  'primary-hover',
  'on-primary',
  'accent',
  'accent-hover',
  'accent-wash',
  'beam',
  'substrate',
  'panel',
  'panel-alt',
  'rule',
  'rule-soft',
  'ink',
  'muted',
  'faint',
  'cat-1',
  'cat-2',
  'cat-3',
  'cat-4',
  'cat-5',
  'cat-6',
  'cat-7',
  'cat-8',
] as const

export const RADIUS_TOKENS = ['sm', 'md', 'lg', 'pill'] as const

export const SPACING_TOKENS = ['1', '2', '3', '4', '5', '6', '7'] as const

export type ColorToken = (typeof COLOR_TOKENS)[number]

/**
 * How each group of tokens is written back as a CSS custom property.
 *
 * `--color-primary`, `--radius-md`, `--sp-3`. The prefixes differ per group, which is
 * why the theme document keys them without one — a document that carried the prefixes
 * would let a caller write `--sp-primary` and mean nothing by it.
 */
export const TOKEN_PREFIX = {
  colors: '--color-',
  radii: '--radius-',
  spacing: '--sp-',
} as const

/**
 * The three type tokens. Not a list, because unlike the others each one has a distinct
 * job and the editor labels them individually.
 */
export const FONT_PROPERTIES = {
  display: '--font-display',
  sans: '--font-sans',
  mono: '--font-mono',
} as const

/**
 * Readable labels for the branding editor.
 *
 * A hex field called `panel-alt` is not self-explanatory to the person choosing a
 * palette, and the token name alone gives no clue that `beam` may never be used for
 * text. The hint is the rule, stated where it can still be followed.
 */
export const COLOR_LABELS: Record<ColorToken, { label: string; hint: string }> = {
  primary: { label: 'Primary', hint: 'The brand colour. Primary buttons, the wordmark.' },
  'primary-hover': { label: 'Primary (hover)', hint: 'A step darker than the primary.' },
  'on-primary': {
    label: 'On primary',
    hint: 'Text and marks sitting on the primary — the Save button’s label. Change it if your primary is pale, or the most-used control in the product becomes unreadable.',
  },
  accent: { label: 'Accent', hint: 'Links, the focus ring, and the one active thing on a screen.' },
  'accent-hover': { label: 'Accent (hover)', hint: 'A step darker than the accent.' },
  'accent-wash': {
    label: 'Accent wash',
    hint: 'The pale tint behind a selected row. Must stay light enough to read ink on.',
  },
  beam: {
    label: 'Beam',
    hint: 'Graphics and chart highlights only — never text. It is not required to meet a contrast ratio, so it must never carry one.',
  },
  substrate: { label: 'Page', hint: 'Behind everything. The panels sit on it.' },
  panel: { label: 'Panel', hint: 'Grids, cards, the header bar.' },
  'panel-alt': { label: 'Panel (alt)', hint: 'Row hover, column-header bands.' },
  rule: { label: 'Rule', hint: 'Borders and grid lines.' },
  'rule-soft': { label: 'Rule (soft)', hint: 'Interior grid lines, between rows.' },
  ink: { label: 'Ink', hint: 'Body text. Needs 7:1 against the panel.' },
  muted: { label: 'Muted', hint: 'Secondary text. Needs 4.5:1 against the panel.' },
  faint: { label: 'Faint', hint: 'Column headers and hints. Needs 4.5:1 against the panel.' },
  'cat-1': { label: 'Series 1', hint: 'Categorical — assigned per cost driver and kept.' },
  'cat-2': { label: 'Series 2', hint: 'Categorical — assigned per cost driver and kept.' },
  'cat-3': { label: 'Series 3', hint: 'Categorical — assigned per cost driver and kept.' },
  'cat-4': { label: 'Series 4', hint: 'Categorical — assigned per cost driver and kept.' },
  'cat-5': { label: 'Series 5', hint: 'Categorical — assigned per cost driver and kept.' },
  'cat-6': { label: 'Series 6', hint: 'Categorical — assigned per cost driver and kept.' },
  'cat-7': { label: 'Series 7', hint: 'Categorical — assigned per cost driver and kept.' },
  'cat-8': { label: 'Series 8', hint: 'Categorical — assigned per cost driver and kept.' },
}

/** Every custom property a theme can write, as one flat list. */
export function allThemeProperties(): string[] {
  return [
    ...COLOR_TOKENS.map((t) => `${TOKEN_PREFIX.colors}${t}`),
    ...RADIUS_TOKENS.map((t) => `${TOKEN_PREFIX.radii}${t}`),
    ...SPACING_TOKENS.map((t) => `${TOKEN_PREFIX.spacing}${t}`),
    ...Object.values(FONT_PROPERTIES),
  ]
}
